import { Fragment, useMemo } from "react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { useReactTable, getCoreRowModel, type ColumnDef, flexRender } from "@tanstack/react-table";
import { Zap, Sparkles, Layers, Users, AlertTriangle, ChevronRight } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/hooks/use-session";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message, formatSnakeCase } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";
import type { ContentAlignmentAPIData } from "@/schema/brand-os/brand-intel";

interface GapRow {
  content_format: string;
  audience_demand: number;
  brand_output: number;
  gap: number;
  priority: string;
}

interface Summary {
  title: string;
  items: string[];
}

const priorityStyles: Record<string, string> = {
  critical: "bg-destructive/10 text-destructive",
  high: "bg-warning/10 text-warning",
  medium: "bg-success/10 text-success",
  normal: "bg-muted text-muted-foreground",
  over_indexed: "bg-muted text-muted-foreground",
};

const recommendationTitles: Record<string, string> = {
  audience_demand: "Audience Demand",
  brand_production: "Brand Production",
  opportunity: "Opportunity",
};

const summaryStyles = [
  { iconBg: "bg-success/10", iconColor: "text-success", Icon: Users, titleColor: "text-success", arrowColor: "text-success" },
  { iconBg: "bg-blue-100/40", iconColor: "text-blue-600", Icon: Layers, titleColor: "text-blue-600", arrowColor: "text-blue-600" },
  { iconBg: "bg-warning/10", iconColor: "text-warning", Icon: Zap, titleColor: "text-foreground", arrowColor: "text-warning" },
];

function GapTable({ rows, brandName }: { rows: GapRow[]; brandName: string }) {
  const columns = useMemo<ColumnDef<GapRow>[]>(
    () => [
      {
        accessorKey: "content_format",
        header: "Content Format",
        cell: ({ row }) => <p className="font-medium text-foreground">{row.original.content_format}</p>,
      },
      {
        accessorKey: "audience_demand",
        header: () => <p className="text-center">Audience Demand</p>,
        cell: ({ row }) => <p className="text-center font-semibold text-success">{Math.round(row.original.audience_demand)} %</p>,
      },
      {
        accessorKey: "brand_output",
        header: () => <p className="text-center">{brandName} Output</p>,
        cell: ({ row }) => <p className="text-center text-muted-foreground">{Math.round(row.original.brand_output)} %</p>,
      },
      {
        accessorKey: "gap",
        header: () => <p className="text-center">Gap</p>,
        cell: ({ row }) => {
          const isNegative = row.original.gap < 0;
          return (
            <p className={cn("text-center font-semibold", isNegative ? "text-destructive" : "text-success")}>
              {isNegative ? "" : "+"}
              {Math.round(row.original.gap)} pts
            </p>
          );
        },
      },
      {
        accessorKey: "priority",
        header: () => <p className="text-right">Priority</p>,
        cell: ({ row }) => {
          const key = row.original.priority.toLowerCase();
          const label = formatSnakeCase(key);
          return (
            <div className="flex justify-end">
              <Badge variant="secondary" className={cn(priorityStyles[key] ?? "bg-muted text-muted-foreground")}>
                {label}
              </Badge>
            </div>
          );
        },
      },
    ],
    [brandName]
  );

  const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="max-h-[540px] overflow-y-auto scrollbar-hidden">
        <table className="w-full caption-bottom text-sm">
          <thead className="sticky top-0 z-10 bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-muted/50 border-border">
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="bg-muted/50 text-xs font-medium h-10 px-4 text-left">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-t border-border hover:bg-muted/30">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="h-24 px-4 text-center text-sm text-muted-foreground">
                  No results.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCards({ summaries }: { summaries: Summary[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      {summaries.map((summary, index) => {
        const style = summaryStyles[index % summaryStyles.length];
        return (
          <Card key={summary.title}>
            <CardContent className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <div className={cn("flex size-8 items-center justify-center rounded-md", style.iconBg)}>
                  <style.Icon className={cn("size-4", style.iconColor)} strokeWidth={2.5} />
                </div>
                <p className={cn("text-sm font-semibold", style.titleColor)}>{summary.title}</p>
              </div>
              <ul className="space-y-2">
                {summary.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <div className="flex w-8 shrink-0 items-center justify-center pt-0.5">
                      <ChevronRight className={cn("size-3", style.arrowColor)} strokeWidth={2.5} />
                    </div>
                    <p>{item}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function TableSummaryContent({ rows, summaries, brandName }: { rows: GapRow[]; summaries: Summary[]; brandName: string }) {
  if (!rows.length) return <LockedContent message={message} className="mb-14" />;
  return (
    <Fragment>
      <GapTable rows={rows} brandName={brandName} />
      {summaries.length > 0 && <SummaryCards summaries={summaries} />}
    </Fragment>
  );
}

function AlignmentContent({ status, rows, summaries, brandName }: { status: QueryStatus; rows: GapRow[]; summaries: Summary[]; brandName: string }) {
  switch (status) {
    case "pending":
      return <AlignmentSkeleton />;
    case "error":
      return <LockedContent message={message} className="mb-14" />;
    case "success":
      return <TableSummaryContent rows={rows} summaries={summaries} brandName={brandName} />;
  }
}

function buildSummaries(data: ContentAlignmentAPIData): Summary[] {
  return (Object.entries(data.recommendations) as [keyof ContentAlignmentAPIData["recommendations"], string[]][])
    .filter(([, items]) => items.length > 0)
    .map(([key, items]) => ({ title: recommendationTitles[key] ?? key, items }));
}

export function AlignmentGap() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchContentAlignment());
  const session = useSession();
  const brandName = session.profile.company.company_name ?? "Brand";

  const rows = (data?.alignment_gaps ?? []).map((item) => ({
    content_format: item.content_name,
    audience_demand: item.audience_demand_percentage,
    brand_output: item.brand_supply_percentage,
    gap: item.gap,
    priority: item.priority,
  }));

  const summaries = data ? buildSummaries(data) : [];

  const misalignedPercent =
    data?.alignment_gaps && data.total_content_types > 0
      ? Math.round((data.alignment_gaps.filter((g) => g.priority.toLowerCase() === "critical" || g.priority.toLowerCase() === "high").length / data.total_content_types) * 100)
      : 0;

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-violet-100/40 p-2">
            <Sparkles className="size-5 text-violet-600" strokeWidth={2.5} />
          </div>
          <div>
            <CardTitle className="text-base font-semibold">Content-Audience Alignment Gap</CardTitle>
            {rows.length > 0 && <p className="text-xs text-muted-foreground mt-0.5">What your audience engages with vs what {brandName} currently produces</p>}
          </div>
        </div>
        {rows.length > 0 && (
          <Badge variant="secondary" className="bg-destructive/10 text-destructive">
            <AlertTriangle className="size-4" strokeWidth={2.5} />
            {misalignedPercent}% misaligned
          </Badge>
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-6">
        <AlignmentContent status={status} rows={rows} summaries={summaries} brandName={brandName} />
      </CardContent>
    </Card>
  );
}

function AlignmentSkeleton() {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="bg-muted/50 px-4 py-2.5 flex gap-12">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-3 w-20" />
        ))}
      </div>
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="px-4 py-3 flex gap-12 border-t border-border">
          {Array.from({ length: 5 }, (_, j) => (
            <Skeleton key={j} className="h-4 w-20" />
          ))}
        </div>
      ))}
    </div>
  );
}
